/** @jsx jsx */

import {
  Fragment as _,
  useContext,
  useState,
  cloneElement,
  useRef,
  useEffect,
} from 'react';
import { css, jsx } from '@emotion/core';

const maxHeight = 240;

export const TextEntry = props => {
  const {
    message,
    messageContainer,
    messageUnderline,
    messageTextColor = '#000',
    messageTextBackgroundColor,
    messageBorderRadius,
    messageBorderColor = '#d2dff9',
    messageFontFamily = 'inherit',
    messageFontSize = 16,
    onUpdate,
  } = props;

  const [textAreaStyle, setTextAreaStyle] = useState<{
    height?: number;
    overflow?: string;
  }>({});

  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const textArea = useRef<HTMLTextAreaElement>(null);
  const textAreaProxy = useRef<HTMLDivElement>(null);

  const updateTextAreaStyle = () => {
    if (!textAreaProxy.current) {
      return;
    }

    const height = textAreaProxy.current.offsetHeight;
    setTextAreaStyle({
      overflow: height < maxHeight ? 'hidden' : 'auto',
    });
  };

  useEffect(() => {
    if (!textAreaProxy.current) {
      return;
    }

    updateTextAreaStyle();
  }, []);

  const sharedStyles = {
    width: '100%',
    height: '100%',
    maxHeight,
    background: 'transparent',
    padding: '5px 10px' || '15px 5px 0px 5px',
    fontFamily: messageFontFamily,
    fontSize: messageFontSize,
    color: messageTextColor,
  };

  const textAreaProxyHTML = message.replace(/\n/g, '<br>');
  const placeholder = !hasFocus ? 'Type your message here...' : '';

  return (
    <div
      style={{
        width: '100%',
      }}
    >
      <div
        style={{
          position: 'relative',
          maxHeight: sharedStyles.maxHeight,
          ...(messageContainer && {
            height: 150,
            padding: `10px ${messageBorderRadius || 20}px 10px 10px`,
            background: messageTextBackgroundColor,
            borderRadius: messageBorderRadius,
            boxShadow: `0 0 0 ${hasFocus ? 3 : 1}px ${
              hasFocus && messageBorderColor === 'transparent'
                ? '#d2dff9'
                : messageBorderColor
            }`,
            transition: 'box-shadow 0.1s',
          }),
        }}
      >
        {messageUnderline && (
          <div
            ref={textAreaProxy}
            style={{
              ...sharedStyles,
              visibility: 'hidden',
              overflow: 'hidden',
            }}
            dangerouslySetInnerHTML={{
              __html: textAreaProxyHTML || '*',
            }}
            aria-hidden="true"
          />
        )}

        <textarea
          ref={textArea}
          placeholder={placeholder}
          css={css({
            position: messageUnderline ? 'absolute' : 'relative',
            left: 0,
            top: 0,
            border: 'none',
            outline: 'none',
            resize: 'none',

            ...sharedStyles,
            ...textAreaStyle,

            textAlign: message || hasFocus ? 'left' : 'center',

            ':focus': {
              border: 'none !important',
              outline: 'none !important',
            },
          })}
          onFocus={() => {
            setHasFocus(true);
          }}
          onBlur={() => {
            setHasFocus(false);
          }}
          onKeyDown={e => {
            //
          }}
          onKeyUp={e => {
            updateTextAreaStyle();
            onUpdate(e.currentTarget.value);
          }}
          aria-label={placeholder}
        />
      </div>

      {messageUnderline && (
        <div
          style={{
            width: '100%',
            height: 1,
            marginTop: 10,
            boxShadow: `0 0 0 ${hasFocus ? 1 : 0}px ${messageBorderColor}`,
            background: messageBorderColor,
            transition: 'box-shadow 0.3s',
          }}
        />
      )}
    </div>
  );
};
